import React, { useRef, useState } from 'react';
import { IHeaderParams } from '@ag-grid-community/core';
import { CustomHeaderProps } from '@ag-grid-community/react';
import { Icon, InputAdornment, OutlinedInput, Tooltip } from '@material-ui/core';
import { EditableGridHeaderEditorBaseProps } from './TextValidationHeaderEditor';
import { IntegerEditorParams } from './IntegerEditor/IntegerEditor.types';
import { ROW_SELECTION_REQUIRED_TEXT, ROW_SELECTIONS_UPDATING_TEXT } from 'src/utils/Domain/Constants';
import massEditstyles from 'src/components/MassEdit/MassEdit.styles';
import { defaultTo, isEmpty, isNil } from 'lodash';

export interface IntegerHeaderEditorProps
  extends IHeaderParams,
  CustomHeaderProps,
  EditableGridHeaderEditorBaseProps,
  IntegerEditorParams {
  regularPosition?: boolean;
  tooltip?: string;
  editType?: string;
}

function formatValue(asPercent: boolean, value: number) {
  return asPercent ? value * 0.01 : value;
}
/** helper to allow typing of whole percentages into percent headitor, to match how the cell edits work */
function toInputRange(percent: boolean, min?: number, max?: number) {
  if (!percent) return { displayMin: min, displayMax: max };

  const displayMin = !isNil(min) ? min * 100 : undefined;
  const displayMax = !isNil(max) ? max * 100 : undefined;
  return { displayMin, displayMax };
}

export const IntegerHeaderEditor = (props: IntegerHeaderEditorProps) => {
  const {
    column,
    getSelectedItems,
    getCellsUpdating,
    min = 0,
    max = undefined,
    nullable = false,
    onApplyEdit,
    percent = false,
    regularPosition = false,
    tooltip
  } = props;

  const [headerValue, setHeaderValue] = useState<number | null>(null);
  const headerInputRef = useRef<HTMLInputElement>(null);
  const { displayMin, displayMax } = toInputRange(percent, min, max);

  const handleApply = () => {
    if (headerInputRef.current?.checkValidity() === false) {
      headerInputRef.current?.reportValidity();
      return;
    }

    headerInputRef.current?.blur();
    const items = getSelectedItems();

    let finalValue;
    if (nullable) {
      finalValue = headerValue === null ? null : formatValue(percent, headerValue);
    } else {
      finalValue = headerValue === null ? 0 : formatValue(percent, headerValue);
    }

    onApplyEdit(finalValue, items, column.getColDef());
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value);

    if (!event.target.checkValidity()) {
      event.target.reportValidity();
    }

    const setToNull = isNaN(value);
    const finalValue = setToNull ? null : value;
    setHeaderValue(finalValue);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleApply();
    }
  };

  const disabled = getSelectedItems().length === 0;
  const isUpdating = getCellsUpdating().length > 0;
  let title = '';
  if (disabled) {
    title = ROW_SELECTION_REQUIRED_TEXT;
  } else if (isUpdating) {
    title = ROW_SELECTIONS_UPDATING_TEXT;
  } else {
    title = tooltip ?? ''
  }
  const position = regularPosition ? 'inherit' : 'absolute';

  // currently only support edittype percent
  const icon = (props.editType == 'percent') ? 'percent' : undefined;
  return (
    <Tooltip title={title}>
      <OutlinedInput
        type="number"
        className={massEditstyles.massEditMuiInput}
        style={{ position }}
        disabled={disabled || isUpdating}
        // cannot send null to input, send empty string instead
        value={headerValue ?? ''}
        inputProps={{
          ref: headerInputRef,
          min: displayMin,
          max: displayMax,
          step: "any",
          onKeyDown: handleKeyDown,
        }}
        endAdornment={((!isEmpty(icon)) ?
          <InputAdornment position="end">
            <Icon className={`fas fa-${icon}`} style={{ fontSize: 12 }} />
          </InputAdornment>
          : undefined
        )}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />
    </Tooltip>
  );
};
